import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { usePageTitle } from '../../../lib/hooks';
import { Button } from '../../../components/ui/Button';
import { Trash2, Lock, Plus, Shield, Edit2, KeyRound } from 'lucide-react';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import {
  useRolesPaginated,
  useDeleteRole,
  useCreateRole,
  useUpdateRole,
  usePermissions,
  useRolePermissions,
  useSetRolePermissions,
  type Role,
  type Permission,
} from '../../../features/rbac/network/rbac.api';
import { useToast } from '../../../components/ui/Toast';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/Dialog';
import { FormField, Input } from '../../../components/ui/FormField';

import { getSession } from '../../../lib/session';

export const Route = createFileRoute('/_auth/settings/roles')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('role.read');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: RolesSettingsPage,
});

// Permission categories (derived from the leading dot-namespace of a permission code, e.g.
// "role.update" -> "ROLE") whose bulk grant via "Toggle Category" needs an explicit confirmation
// step -- they cover role/member management and billing, the categories called out by the audit
// (item 27) as easy to accidentally over-grant in one click.
const DANGEROUS_PERMISSION_CATEGORIES = new Set(['ROLE', 'MEMBER', 'BILLING']);

type PendingToggleGroup = {
  category: string;
  permIds: string[];
};

function RolesSettingsPage() {
  usePageTitle('Role Management');
  const [deactivateRole, setDeactivateRole] = useState<Role | null>(null);
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useRolesPaginated(page, 25);
  const deleteMutation = useDeleteRole();
  const { toast } = useToast();

  // Create Role State
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const createMutation = useCreateRole();

  // Edit Role State
  const [editOpen, setEditOpen] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDesc, setEditRoleDesc] = useState('');
  const updateMutation = useUpdateRole();

  // Permissions Modal State
  const [permsOpen, setPermsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const { data: allPermsData, isLoading: loadingPerms } = usePermissions(1, 1000);
  const { data: assignedPermsData, isLoading: loadingAssigned } = useRolePermissions(selectedRole?.id);
  const [checkedPerms, setCheckedPerms] = useState<string[]>([]);
  const setPermissionsMutation = useSetRolePermissions();

  // Sync checked permissions when assigned permissions load
  React.useEffect(() => {
    if (assignedPermsData?.data) {
      setCheckedPerms(assignedPermsData.data);
    } else {
      setCheckedPerms([]);
    }
  }, [assignedPermsData, permsOpen]);

  const handleDeactivate = async () => {
    if (!deactivateRole) return;
    try {
      await deleteMutation.mutateAsync(deactivateRole.id);
      toast({ type: 'success', title: 'Role Deactivated' });
      setDeactivateRole(null);
    } catch (err) {
      console.error('Failed to deactivate role:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate role';
      if (errorMessage === 'Cannot deactivate role with active users') {
        toast({ type: 'error', title: 'Error', description: 'Cannot deactivate role with active users' });
      } else {
        toast({ type: 'error', title: 'Error', description: errorMessage });
      }
      setDeactivateRole(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    try {
      await createMutation.mutateAsync({ name: newRoleName, description: newRoleDesc });
      toast({ type: 'success', title: 'Role Created' });
      setCreateOpen(false);
      setNewRoleName('');
      setNewRoleDesc('');
    } catch (err) {
      console.error('Failed to create role:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create role';
      toast({ type: 'error', title: 'Error', description: errorMessage });
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRole || !editRoleName.trim()) return;
    try {
      await updateMutation.mutateAsync({
        id: editRole.id,
        body: { name: editRoleName, description: editRoleDesc },
      });
      toast({ type: 'success', title: 'Role Updated' });
      setEditOpen(false);
      setEditRole(null);
    } catch (err) {
      console.error('Failed to update role:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update role';
      toast({ type: 'error', title: 'Error', description: errorMessage });
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    try {
      await setPermissionsMutation.mutateAsync({
        id: selectedRole.id,
        permissionIds: checkedPerms,
      });
      toast({ type: 'success', title: 'Permissions Saved' });
      setPermsOpen(false);
      setSelectedRole(null);
    } catch (err) {
      console.error('Failed to save permissions:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save permissions';
      toast({ type: 'error', title: 'Error', description: errorMessage });
    }
  };

  // Group all available permissions logically by dot-namespace
  const groupedPerms = React.useMemo(() => {
    if (!allPermsData?.data) return {} as Record<string, Permission[]>;
    const groups: Record<string, Permission[]> = {};
    for (const perm of allPermsData.data) {
      const parts = perm.code.split('.');
      const category = parts[0] ? parts[0].toUpperCase() : 'GENERAL';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(perm);
    }
    return groups;
  }, [allPermsData]);

  const handleTogglePerm = (permId: string) => {
    setCheckedPerms((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const [pendingToggleGroup, setPendingToggleGroup] = useState<PendingToggleGroup | null>(null);

  const applyToggleGroup = (permIds: string[], checked: boolean) => {
    if (checked) {
      setCheckedPerms((prev) => [...new Set([...prev, ...permIds])]);
    } else {
      setCheckedPerms((prev) => prev.filter((id) => !permIds.includes(id)));
    }
  };

  const handleToggleGroup = (category: string, permIds: string[], checked: boolean) => {
    // Only the grant direction is risky (silently accumulating dangerous permissions in bulk);
    // revoking a whole category is never gated.
    if (checked && DANGEROUS_PERMISSION_CATEGORIES.has(category)) {
      setPendingToggleGroup({ category, permIds });
      return;
    }
    applyToggleGroup(permIds, checked);
  };

  const confirmPendingToggleGroup = () => {
    if (!pendingToggleGroup) return;
    applyToggleGroup(pendingToggleGroup.permIds, true);
    setPendingToggleGroup(null);
  };

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Role Management"
        subtitle="Manage system roles and their permissions."
        action={
          <Button onClick={() => setCreateOpen(true)} variant="primary" size="sm" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            Create Custom Role
          </Button>
        }
      />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load roles</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <>
              <div className="border border-border rounded-[14px] bg-surface-2 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-surface-3 border-b border-border text-sm text-text-muted">
                    <tr>
                      <th className="px-4 py-3 font-medium">Role Name</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Description</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-sm">
                    {data?.data?.map((role) => (
                      <tr key={role.id} className="hover:bg-surface-3/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-text-primary flex items-center gap-2">
                          <Shield className="h-4 w-4 text-accent-500" />
                          {role.name}
                        </td>
                        <td className="px-4 py-3">
                          {role.type === 'system' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-500/10 text-accent-600 border border-accent-500/20">
                              <Lock className="h-3 w-3" /> Prebuilt System
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-success-500/10 text-success-600 border border-success-500/20">
                              Custom Role
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-text-muted text-sm">{role.description || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1.5">
                            {role.type !== 'system' ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditRole(role);
                                    setEditRoleName(role.name);
                                    setEditRoleDesc(role.description || '');
                                    setEditOpen(true);
                                  }}
                                  className="h-8 px-2.5 text-text-secondary hover:text-text-primary hover:bg-surface-3"
                                  title="Edit role details"
                                >
                                  <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRole(role);
                                    setPermsOpen(true);
                                  }}
                                  className="h-8 px-2.5"
                                  title="Manage permission set"
                                >
                                  <KeyRound className="h-3.5 w-3.5 mr-1" /> Permissions
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => setDeactivateRole(role)}
                                  className="h-8 w-8 p-0 text-danger-text hover:text-danger-text hover:bg-danger-500/10"
                                  title="Deactivate role"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <span className="text-xs text-text-muted italic flex items-center gap-1 px-2 py-1 select-none">
                                <Lock className="h-3 w-3" /> System Immutable
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.data?.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-text-muted">
                          No roles found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {data?.meta && (
                <div className="flex items-center justify-between px-1 py-3 text-sm text-text-secondary">
                  <span>Page {data.meta.page} of {Math.max(1, Math.ceil(data.meta.total / data.meta.limit))}</span>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                    <Button variant="secondary" size="sm" disabled={page * data.meta.limit >= data.meta.total} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}

              {/* Create Role Dialog */}
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-md bg-surface-1">
                  <DialogHeader>
                    <DialogTitle>Create Custom Role</DialogTitle>
                    <DialogDescription>
                      Add a new authorization role to assign employees. You can manage its exact permissions in the next step.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 py-2">
                    <FormField label="Role Name" required>
                      <Input
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        placeholder="e.g. branch_operations_lead"
                        required
                      />
                    </FormField>
                    <FormField label="Description">
                      <textarea
                        value={newRoleDesc}
                        onChange={(e) => setNewRoleDesc(e.target.value)}
                        placeholder="Operations lead for auditing ledger reconciliations."
                        className="w-full text-sm p-3 bg-surface-3 border border-border rounded-[6px] focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 min-h-[80px] resize-y"
                      />
                    </FormField>
                    <DialogFooter className="pt-2">
                      <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary" loading={createMutation.isPending}>
                        Create Role
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Edit Role Dialog */}
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-md bg-surface-1">
                  <DialogHeader>
                    <DialogTitle>Edit Custom Role</DialogTitle>
                    <DialogDescription>
                      Update the descriptive metadata of your custom-built role.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpdate} className="space-y-4 py-2">
                    <FormField label="Role Name" required>
                      <Input
                        value={editRoleName}
                        onChange={(e) => setEditRoleName(e.target.value)}
                        required
                      />
                    </FormField>
                    <FormField label="Description">
                      <textarea
                        value={editRoleDesc}
                        onChange={(e) => setEditRoleDesc(e.target.value)}
                        className="w-full text-sm p-3 bg-surface-3 border border-border rounded-[6px] focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 min-h-[80px] resize-y"
                      />
                    </FormField>
                    <DialogFooter className="pt-2">
                      <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary" loading={updateMutation.isPending}>
                        Save Changes
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Set Permissions Dialog */}
              <Dialog open={permsOpen} onOpenChange={setPermsOpen}>
                <DialogContent className="sm:max-w-2xl bg-surface-1 max-h-[85vh] flex flex-col">
                  <DialogHeader className="px-1">
                    <DialogTitle className="flex items-center gap-2">
                      <KeyRound className="h-5 w-5 text-accent-500" />
                      Manage Permissions — {selectedRole?.name}
                    </DialogTitle>
                    <DialogDescription>
                      Select which capabilities this custom role should possess across the platform. Permissions are grouped logically by module namespace.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto px-1 py-4 space-y-6">
                    {loadingPerms || loadingAssigned ? (
                      <div className="space-y-4 py-8">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-24 w-full" />
                      </div>
                    ) : (
                      Object.entries(groupedPerms).map(([category, perms]) => {
                        const permIds = perms.map((p) => p.id);
                        const allChecked = permIds.every((id) => checkedPerms.includes(id));
                        const someChecked = permIds.some((id) => checkedPerms.includes(id)) && !allChecked;

                        return (
                          <div key={category} className="border border-border rounded-xl bg-surface-2 p-4 shadow-sm">
                            <div className="flex items-center justify-between border-b border-border pb-2.5 mb-3">
                              <h3 className="font-bold text-text-primary text-xs uppercase tracking-wider">
                                {category} ({perms.length})
                              </h3>
                              <label className="flex items-center gap-1.5 text-xs text-accent-500 hover:text-accent-600 font-semibold cursor-pointer">
                                <Input
                                  type="checkbox"
                                  className="rounded border-border focus:ring-accent-500 text-accent-500 h-3.5 w-3.5 cursor-pointer"
                                  checked={allChecked}
                                  ref={(el) => {
                                    if (el) el.indeterminate = someChecked;
                                  }}
                                  onChange={(e) => handleToggleGroup(category, permIds, e.target.checked)}
                                />
                                Toggle Category
                              </label>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                              {perms.map((perm) => {
                                const isChecked = checkedPerms.includes(perm.id);
                                return (
                                  <label
                                    key={perm.id}
                                    // allow-raw-sql: "select-none" is a Tailwind class name, not SQL — heuristic false positive
                                    className={`flex items-start gap-2.5 p-2 rounded-lg border transition-all cursor-pointer select-none text-xs ${
                                      isChecked
                                        ? 'bg-accent-500/5 border-accent-500/20 hover:bg-accent-500/10'
                                        : 'bg-surface-3/50 border-transparent hover:bg-surface-3'
                                    }`}
                                  >
                                    <Input
                                      type="checkbox"
                                      className="rounded border-border focus:ring-accent-500 text-accent-500 h-3.5 w-3.5 mt-0.5 cursor-pointer"
                                      checked={isChecked}
                                      onChange={() => handleTogglePerm(perm.id)}
                                    />
                                    <div>
                                      <p className="font-mono font-medium text-text-primary text-[11px]">{perm.code}</p>
                                      {perm.description && (
                                        <p className="text-[10px] text-text-muted mt-0.5 leading-normal">{perm.description}</p>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <DialogFooter className="border-t border-border pt-4 px-1">
                    <Button type="button" variant="secondary" onClick={() => setPermsOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleSavePermissions}
                      loading={setPermissionsMutation.isPending}
                      disabled={loadingPerms || loadingAssigned}
                    >
                      Save Permission Set
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <ConfirmDialog
                open={!!deactivateRole}
                title="Deactivate Role?"
                description={`Are you sure you want to deactivate the '${deactivateRole?.name}' role? Users currently assigned to this role may lose access.`}
                confirmLabel={deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                onConfirm={handleDeactivate}
                onCancel={() => setDeactivateRole(null)}
              />

              <ConfirmDialog
                open={!!pendingToggleGroup}
                title={`Grant all ${pendingToggleGroup?.category ?? ''} permissions?`}
                description={`This grants all ${pendingToggleGroup?.permIds.length ?? 0} permission(s) in the ${pendingToggleGroup?.category ?? ''} category at once, including sensitive actions such as role, member, or billing management. Review individually if you only need a subset.`}
                confirmLabel="Grant Category"
                onConfirm={confirmPendingToggleGroup}
                onCancel={() => setPendingToggleGroup(null)}
              />
            </>
          );
        })()}
      </div>
    </div>
  );
}
